import { Check, ChevronRight } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface MenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Shown faintly on the right (a shortcut or a count). */
  hint?: string;
  onSelect?: () => void;
  submenu?: MenuEntry[];
  danger?: boolean;
  disabled?: boolean;
  /** Check mark for the current choice, or for a toggle. */
  checked?: boolean;
  /** Toggles: choosing it leaves the menu open. */
  keepOpen?: boolean;
}

export type MenuEntry = MenuItem | 'separator';

const isItem = (e: MenuEntry): e is MenuItem => e !== 'separator';

/**
 * A popup menu at a screen point: keyboard navigation, submenus that fly out to the side,
 * and it stays inside the window. Rendered in a portal so dialogs and scroll areas can't clip it.
 */
export function Menu({
  items,
  x,
  y,
  onClose,
  ariaLabel,
  minWidth,
  /** For submenus: close just this level and go back to the parent. */
  onBack,
  /** For submenus: where to go when there's no room on the right. */
  flipX,
}: {
  items: MenuEntry[];
  x: number;
  y: number;
  onClose: () => void;
  ariaLabel?: string;
  minWidth?: number;
  onBack?: () => void;
  flipX?: number;
}) {
  const uid = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });
  const selectable = items.map((e, i) => (isItem(e) && !e.disabled ? i : -1)).filter((i) => i >= 0);
  const [active, setActive] = useState(() => {
    const current = items.findIndex((e) => isItem(e) && e.checked && !e.disabled);
    return current >= 0 ? current : selectable[0] ?? -1;
  });
  const [open, setOpen] = useState<{ index: number; x: number; y: number; flipX: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 8;
    let left = x;
    let top = y;
    if (left + r.width > window.innerWidth - pad) left = flipX !== undefined ? flipX - r.width : window.innerWidth - pad - r.width;
    if (top + r.height > window.innerHeight - pad) top = Math.max(pad, window.innerHeight - pad - r.height);
    setPos({ left: Math.max(pad, left), top: Math.max(pad, top), ready: true });
  }, [x, y, flipX]);
  // Focus once visible: a visibility:hidden element can't take focus.
  useEffect(() => {
    if (pos.ready) ref.current?.focus();
  }, [pos.ready]);

  // Top level: close on a press outside every menu, when the user scrolls the page, on resize,
  // or when the window loses focus. (Not on every `scroll` event: toggling a table column
  // changes the layout and fires one.)
  useEffect(() => {
    if (onBack) return;
    const outside = (e: Event) => {
      if (!(e.target as Element).closest?.('[data-menu]')) onClose();
    };
    const close = () => onClose();
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('wheel', outside, { capture: true, passive: true });
    document.addEventListener('touchmove', outside, { capture: true, passive: true });
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('wheel', outside, true);
      document.removeEventListener('touchmove', outside, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [onClose, onBack]);

  const openSub = (i: number) => {
    const row = ref.current?.querySelector<HTMLElement>(`[data-index="${i}"]`);
    if (!row) return;
    const r = row.getBoundingClientRect();
    setOpen({ index: i, x: r.right - 4, y: r.top - 5, flipX: r.left + 4 });
  };

  const choose = (i: number) => {
    const it = items[i];
    if (!it || !isItem(it) || it.disabled) return;
    if (it.submenu) return openSub(i);
    if (!it.keepOpen) onClose();
    it.onSelect?.();
  };

  const move = (d: 1 | -1) => {
    if (!selectable.length) return;
    const at = selectable.indexOf(active);
    setActive(selectable[(at + d + selectable.length) % selectable.length]);
    setOpen(null);
  };

  return createPortal(
    <>
      <div
        ref={ref}
        className="menu"
        role="menu"
        aria-label={ariaLabel}
        data-menu
        tabIndex={-1}
        style={{ left: pos.left, top: pos.top, minWidth, visibility: pos.ready ? 'visible' : 'hidden' }}
        aria-activedescendant={active >= 0 ? `${uid}-${active}` : undefined}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (open) return; // the submenu has focus
          e.stopPropagation();
          if (e.key === 'ArrowDown') move(1);
          else if (e.key === 'ArrowUp') move(-1);
          else if (e.key === 'Home') setActive(selectable[0] ?? -1);
          else if (e.key === 'End') setActive(selectable.at(-1) ?? -1);
          else if (e.key === 'Enter' || e.key === ' ') choose(active);
          else if (e.key === 'ArrowRight') {
            const it = items[active];
            if (it && isItem(it) && it.submenu) openSub(active);
          } else if (e.key === 'ArrowLeft' && onBack) onBack();
          else if (e.key === 'Escape') (onBack ?? onClose)();
          else if (e.key === 'Tab') onClose();
          else return;
          e.preventDefault();
        }}
      >
        {items.map((it, i) =>
          it === 'separator' ? (
            <div key={`sep${i}`} className="menu-sep" role="separator" />
          ) : (
            <div
              key={it.key}
              id={`${uid}-${i}`}
              data-index={i}
              role={it.keepOpen ? 'menuitemcheckbox' : it.checked !== undefined ? 'menuitemradio' : 'menuitem'}
              aria-checked={it.checked}
              aria-disabled={it.disabled || undefined}
              aria-haspopup={it.submenu ? 'menu' : undefined}
              aria-expanded={it.submenu ? open?.index === i : undefined}
              className={`menu-item${i === active ? ' active' : ''}${it.danger ? ' danger' : ''}${it.disabled ? ' disabled' : ''}`}
              onPointerEnter={() => {
                if (it.disabled) return;
                setActive(i);
                if (it.submenu) openSub(i);
                else setOpen(null);
              }}
              onClick={() => choose(i)}
            >
              <span className="menu-icon">{it.checked ? <Check size={14} /> : it.icon}</span>
              <span className="menu-label">{it.label}</span>
              {it.hint && <span className="menu-hint">{it.hint}</span>}
              {it.submenu && <ChevronRight size={14} className="menu-hint" />}
            </div>
          ),
        )}
      </div>
      {open && isItem(items[open.index]) && (
        <Menu
          items={(items[open.index] as MenuItem).submenu!}
          x={open.x}
          y={open.y}
          flipX={open.flipX}
          ariaLabel={typeof (items[open.index] as MenuItem).label === 'string' ? ((items[open.index] as MenuItem).label as string) : undefined}
          onClose={onClose}
          onBack={() => {
            setOpen(null);
            ref.current?.focus();
          }}
        />
      )}
    </>,
    document.body,
  );
}
